import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { MenuOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RankingAPI } from "../../API/RankingAPI";

const READ_ONLY_COLUMNS = ["spec_type", "card_no"];

function SortableRow({ item, onToggle }) {
  return (
    <div className={"field-item field-item-active"}>
      <button
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        type="button"
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>
      <p className="field-item-label">{item.label}</p>
      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={READ_ONLY_COLUMNS.includes(item.id)}
      />
    </div>
  );
}

function SortableItem({ item, onToggle, onRankChange }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: item.rank === null });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.4 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="field-item">
      <button
        ref={setActivatorNodeRef}
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        {...attributes}
        {...listeners}
        type="button"
        tabIndex={-1}
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>

      <p className="field-item-label" title="Drag to reorder">
        {item.label}
      </p>

      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={READ_ONLY_COLUMNS.includes(item.id)}
      />
    </div>
  );
}

export const SelectColumns = ({
  open,
  setOpen,
  messageToast,
  setSelectedColumns,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const fetchColumnConfig = useCallback(async () => {
    setSpinning(true);
    try {
      const response = await RankingAPI.getColumnConfig();
      if (response?.data?.status === true) {
        const configColumns = response.data.all_columns
          .filter(
            (col) =>
              col.column_key !== "status" &&
              col.column_key !== "have_comments" &&
              col.column_key !== "is_drag" &&
              col.column_key !== "assigned_on" &&
              col.column_key !== "assigned_to",
          )
          .map((col) => {
            const existCol = response.data?.visible_columns?.find(
              (c) => c.column_key === col.column_key,
            );
            return {
              id: col.column_key,
              label: col.label,
              rank: existCol ? existCol.display_order : null,
            };
          })
          .sort((a, b) => {
            const orderA =
              a.rank !== null ? parseInt(a.rank) : Number.MAX_SAFE_INTEGER;
            const orderB =
              b.rank !== null ? parseInt(b.rank) : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
        setItems(configColumns || []);
      }
    } catch (error) {
      messageToast("error", "Failed to fetch column configuration.");
    } finally {
      setSpinning(false);
    }
  }, [messageToast]);

  useEffect(() => {
    if (open) {
      fetchColumnConfig();
    }
  }, [open, fetchColumnConfig]);

  const shown = useMemo(
    () => items.filter((i) => i.rank !== null).sort((a, b) => a.rank - b.rank),
    [items],
  );
  const hidden = useMemo(() => items.filter((i) => i.rank === null), [items]);

  const shownIds = shown.map((i) => i.id);

  const reassignRanks = (list) =>
    list.map((item, idx) => ({ ...item, rank: idx + 1 }));

  const activeItem = items.find((i) => i.id === activeId);

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = shown.findIndex((i) => i.id === active.id);
    const newIndex = shown.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(shown, oldIndex, newIndex);

    const updatedShown = reassignRanks(reordered);
    setItems([...updatedShown, ...hidden]);
  }

  const toggleItem = (id) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        if (i.rank !== null) return { ...i, rank: null };
        const maxRank = Math.max(0, ...prev.map((j) => j.rank || 0));
        return { ...i, rank: maxRank + 1 };
      }),
    );
  };

  const updateRank = (id, desiredRank) => {
    setItems((prev) => {
      const enabled = prev
        .filter((i) => i.rank !== null)
        .sort((a, b) => a.rank - b.rank);
      const disabled = prev.filter((i) => i.rank === null);

      const from = enabled.findIndex((i) => i.id === id);
      if (from === -1) return prev;

      const to = Math.min(Math.max(desiredRank, 1), enabled.length) - 1;
      const moved = arrayMove(enabled, from, to);
      const reRanked = reassignRanks(moved);

      return [...reRanked, ...disabled];
    });
  };

  function applyFun() {
    setSpinning(true);
    const bodyData = {
      columns: items
        .filter((i) => i.rank !== null)
        .map((i) => ({ column_key: i.id, display_order: i.rank })),
    };
    RankingAPI.updateColumnConfig(bodyData)
      .then((response) => {
        if (response?.data?.status === true) {
          setSelectedColumns(items.filter((i) => i.rank !== null));
          messageToast("success", "Column configuration updated successfully.");
        } else {
          messageToast("error", "Failed to update column configuration.");
        }
      })
      .catch((error) => {
        messageToast("error", "Failed to update column configuration.");
      })
      .finally(() => {
        setSpinning(false);
        setOpen(false);
      });
  }

  return (
    <Modal
      open={open}
      okText="Apply"
      cancelText="Clear All"
      onCancel={() => setOpen(false)}
      onOk={applyFun}
      style={{
        minWidth: "800px",
      }}
      footer={[
        <Button key="back" type="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
          loading={spinning}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "25px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Add Columns</Title>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          <div>
            <p className="items-section-title">Shown</p>
            <div className="fields-overlay-drag-container">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                modifiers={[
                  restrictToVerticalAxis,
                  restrictToFirstScrollableAncestor,
                ]}
              >
                <SortableContext
                  items={shownIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div>
                    {shown.map((item) => (
                      <SortableItem
                        key={item.id}
                        item={item}
                        onToggle={toggleItem}
                        onRankChange={updateRank}
                      />
                    ))}
                    {shown.length === 0 && (
                      <p className="empty-item">
                        No enabled items. Toggle some back on below.
                      </p>
                    )}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeItem ? (
                    <SortableRow item={activeItem} onToggle={toggleItem} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>

          <div className="mt-1">
            <p className="items-section-title">Hidden</p>
            <div>
              {hidden.length ? (
                hidden.map((item) => (
                  <div key={item.id} className="field-item">
                    <div className="field-item-label">{item.label}</div>
                    <Switch
                      size="small"
                      id={`sw-${item.id}`}
                      checked={item.rank !== null}
                      onChange={() => toggleItem(item.id)}
                      className="field-item-switch"
                      disabled={READ_ONLY_COLUMNS.includes(item.id)}
                    />
                  </div>
                ))
              ) : (
                <p className="empty-item">No hidden items.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
